import { LoginForm } from '@/components/auth/login-form';
import { completeCredentialLogin } from '@/components/auth/login-continuation';
import type { LoginCreditOfferContext } from '@/components/auth/login-credit-offer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { m } from '@/locale/paraglide/messages';
import { useRouter } from '@tanstack/react-router';
import type { ReactElement } from 'react';

interface LoginModalProps {
  callbackUrl?: string;
  creditOffer?: LoginCreditOfferContext;
  description?: string | null;
  onGoogleSignInStart?: () => void;
  onSuccess?: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  title?: string;
  trigger?: ReactElement;
}

export function LoginModal({
  callbackUrl,
  creditOffer,
  description,
  onGoogleSignInStart,
  onSuccess,
  onOpenChange,
  open,
  title,
  trigger,
}: LoginModalProps) {
  const router = useRouter();
  const hasWideVideoCreditOffer =
    creditOffer?.kind === 'video' && Boolean(creditOffer.videoTrial);

  const handleSuccess = () => {
    completeCredentialLogin({
      callbackUrl,
      close: () => onOpenChange(false),
      navigate: (to) => router.navigate({ to }),
      onSuccess,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger render={trigger} />}
      <DialogContent
        className={cn(
          'max-w-[calc(100%-1.5rem)] overflow-visible border-0 bg-transparent p-0 shadow-none ring-0',
          hasWideVideoCreditOffer
            ? 'sm:w-[calc(100%-1.5rem)] sm:max-w-[760px] [&_[data-slot=auth-social-login]]:mx-auto [&_[data-slot=auth-social-login]]:max-w-[324px]'
            : 'sm:max-w-[380px]'
        )}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{title ?? m.auth_login_sign_in()}</DialogTitle>
        </DialogHeader>
        <LoginForm
          active={open}
          callbackUrl={callbackUrl}
          creditOffer={creditOffer ?? { kind: 'signup' }}
          description={description}
          onGoogleSignInStart={onGoogleSignInStart}
          onSuccess={handleSuccess}
          headerLabel={title}
          presentation="modal"
          className="border-0 shadow-none"
          showBottomLink={false}
        />
      </DialogContent>
    </Dialog>
  );
}
